import { useCallback, useState } from "react";
import { useLocation } from "react-router-dom";
import styled from "styled-components";

import { Icon, icons } from "../../../components/Icon";
import { pushAdTrackingEvent } from "../../../utils/adTracking";
import { BackButton, PrimaryButton } from "../../AuthNew/shared/authStyles";
import { motion } from "../../AuthNew/shared/motionUtils";
import {
  ButtonGroup,
  ModalTitle,
  slideInRight,
} from "../shared/onboardingStyles";

// Progress Bar (Rectangle 5) - matches other steps
const ProgressBarContainer = styled.div`
  width: 100%;
  height: 10px;
  background: #eef1f7;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 0;
  position: relative;
`;

const ProgressBarFill = styled(motion.div)`
  height: 100%;
  background: #aa8ef6;
  border-radius: 4px;
  transition: width 0.5s ease-in-out;
  position: absolute;
  top: 0;
  left: 0;
`;

// Link Share Section
const LinkShareSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
  max-width: 400px;
`;

const LinkShareHeader = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-start;
  width: 100%;
`;

const LinkShareIconContainer = styled.div`
  width: 40px;
  height: 40px;
  background: #aa8ef6;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const LinkShareTextContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const LinkShareTitle = styled.p`
  font-family: "Mona Sans", sans-serif;
  font-size: 14px;
  font-weight: 700;
  line-height: 21px;
  color: #000e2a;
  margin: 0;
`;

const LinkShareDescription = styled.p`
  font-family: "Mona Sans", sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
  color: #353a46;
  margin: 0;
`;

const LinkInputContainer = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  height: 40px;
  border: 1px solid #999faa;
  border-radius: 12px;
  padding: 4px 4px 4px 12px;
  background: white;
`;

const LinkInput = styled.input`
  flex: 1;
  border: none;
  outline: none;
  font-family: "Mona Sans", sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 21px;
  color: #000e2a;
  background: transparent;
  min-width: 0;

  &::placeholder {
    color: #666e7f;
  }
`;

const CopyLinkButton = styled(motion.button)`
  height: 100%;
  padding: 4px 12px;
  background: #b8e0fe;
  border: none;
  border-radius: 10px;
  font-family: "Mona Sans", sans-serif;
  font-size: 14px;
  font-weight: 700;
  line-height: 21px;
  color: #2266ef;
  cursor: pointer;
  white-space: nowrap;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: #a0d4fe;
  }
`;

const DividerText = styled.p`
  font-family: "Mona Sans", sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
  color: #4b4e4f;
  text-align: center;
  width: 100%;
  margin: 0;
`;

// Email Input Section
const EmailInputContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  width: 100%;
  max-width: 400px;
`;

const EmailInputField = styled.input`
  width: 100%;
  height: 40px;
  border: 1px solid #999faa;
  border-radius: 12px;
  padding: 10px 12px;
  font-family: "Mona Sans", sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 21px;
  color: #000e2a;
  background: white;
  outline: none;
  margin-bottom: 12px;

  &:last-of-type {
    margin-bottom: 0;
  }

  &::placeholder {
    color: #666e7f;
  }

  &:focus {
    border-color: #2266ef;
  }
`;

const AddEmailButton = styled(motion.button)`
  display: flex;
  gap: 8px;
  align-items: center;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  margin-top: 0;
  width: fit-content;
`;

const AddEmailText = styled.p`
  font-family: "Mona Sans", sans-serif;
  font-size: 14px;
  font-weight: 700;
  line-height: 21px;
  color: #000e2a;
  margin: 0;
`;

const SkipText = styled.p`
  font-family: "Mona Sans", sans-serif;
  font-size: 14px;
  font-weight: 700;
  line-height: 21px;
  color: #353a46;
  text-align: center;
  width: 100%;
  margin: 0;
  cursor: pointer;

  &:hover {
    color: #000e2a;
  }
`;

const Step3InviteTeam = ({
  data,
  onChange,
  onNext,
  onBack,
  userInfo,
  selectedOrganization,
  progress = 66.67, // Default progress for step 4 of 6
}) => {
  const location = useLocation();
  const [emailInputs, setEmailInputs] = useState([""]);
  const [copySuccess, setCopySuccess] = useState(false);

  const inviteLink = `https://fieldservicely.com/enterprise`; // Replace with actual link

  const handleCopyLink = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(inviteLink);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  }, [inviteLink]);

  const handleEmailChange = useCallback(
    (index, value) => {
      const newInputs = [...emailInputs];
      newInputs[index] = value;
      setEmailInputs(newInputs);
    },
    [emailInputs],
  );

  const handleAddEmail = useCallback(() => {
    // Limit to maximum 3 email inputs
    if (emailInputs.length >= 3) {
      return;
    }
    setEmailInputs([...emailInputs, ""]);
  }, [emailInputs]);

  // Email validation
  const isValidEmail = useCallback(email => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.trim());
  }, []);

  const handleSkip = useCallback(() => {
    onChange({ inviteEmails: [], skipInvite: true });
    onNext();
  }, [onChange, onNext]);

  const handleSubmit = useCallback(() => {
    // Filter valid emails (limit to maximum 3)
    const validEmails = emailInputs
      .map(email => email.trim())
      .filter(email => email && isValidEmail(email))
      .slice(0, 3); // Ensure we never exceed 3 emails

    // Track team invitation step
    pushAdTrackingEvent({
      event: "onboarding_team_invited",
      location,
      selectedOrganization,
      user_id: userInfo?.user_id || null,
      full_name:
        userInfo?.first_name && userInfo?.last_name
          ? `${userInfo.first_name} ${userInfo.last_name}`
          : userInfo?.first_name || null,
      email: userInfo?.email || null,
    });

    if (validEmails.length > 0) {
      onChange({ inviteEmails: validEmails });
      onNext();
    } else {
      handleSkip();
    }
  }, [
    emailInputs,
    isValidEmail,
    onChange,
    onNext,
    handleSkip,
    location,
    selectedOrganization,
    userInfo,
  ]);

  const hasValidEmails = emailInputs.some(
    email => email.trim() && isValidEmail(email.trim()),
  );

  return (
    <motion.div
      variants={slideInRight}
      initial="initial"
      animate="animate"
      exit="exit"
    >
      {/* Progress Bar */}
      <ProgressBarContainer>
        <ProgressBarFill
          initial={{ width: 0 }}
          animate={{ width: `${progress}%` }}
          transition={{ duration: 0.5, ease: "easeInOut" }}
        />
      </ProgressBarContainer>

      {/* Title */}
      <div style={{ marginTop: "32px" }}>
        <ModalTitle>Invite your team</ModalTitle>
      </div>

      {/* Link Share Section */}
      <LinkShareSection style={{ marginTop: "32px" }}>
        {/* <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
          <LinkShareHeader>
            <LinkShareIconContainer>
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M10 13C10.4295 13.5741 10.9774 14.0491 11.6066 14.3929C12.2357 14.7367 12.9315 14.9411 13.6467 14.9923C14.3618 15.0435 15.0796 14.9403 15.7513 14.6897C16.4231 14.4392 17.0331 14.047 17.54 13.54L20.54 10.54C21.4508 9.59695 21.9548 8.33394 21.9434 7.02296C21.932 5.71198 21.4061 4.45791 20.4791 3.53087C19.5521 2.60383 18.298 2.07799 16.987 2.0666C15.676 2.0552 14.413 2.55918 13.47 3.46997L11.75 5.17997"
                  stroke="#6c2ef2"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M14 11C13.5705 10.4259 13.0226 9.9508 12.3934 9.60704C11.7642 9.26328 11.0685 9.05886 10.3533 9.00766C9.63816 8.95645 8.92037 9.0597 8.24861 9.31022C7.57685 9.56074 6.96684 9.9529 6.45996 10.46L3.45996 13.46C2.54917 14.403 2.04519 15.666 2.05659 16.977C2.06798 18.288 2.59382 19.5421 3.52086 20.4691C4.4479 21.3961 5.70197 21.922 7.01295 21.9334C8.32393 21.9448 9.58694 21.4408 10.53 20.53L12.24 18.82"
                  stroke="#6c2ef2"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </LinkShareIconContainer>
            <LinkShareTextContainer>
              <LinkShareTitle>Share your workspace instantly</LinkShareTitle>
              <LinkShareDescription>
                Copy this link and share it with anyone you want to join
              </LinkShareDescription>
            </LinkShareTextContainer>
          </LinkShareHeader> */}
        {/* <LinkInputContainer>
            <LinkInput type="text" value={inviteLink} readOnly />
            <CopyLinkButton
              type="button"
              onClick={handleCopyLink}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {copySuccess ? "Copied!" : "Copy link"}
            </CopyLinkButton>
          </LinkInputContainer> */}
        {/* </div> */}

        {/* Divider */}
        {/* <DividerText>Or send direct invite</DividerText> */}

        {/* Email Inputs */}
        <EmailInputContainer>
          {emailInputs.map((email, index) => (
            <EmailInputField
              key={index}
              type="email"
              value={email}
              onChange={e => handleEmailChange(index, e.target.value)}
              placeholder="teammate@company.com"
            />
          ))}
        </EmailInputContainer>

        {/* Add Another Email Button - Hide when 3 emails reached */}
        {emailInputs.length < 3 && (
          <AddEmailButton
            type="button"
            onClick={handleAddEmail}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <Icon component={icons.plus} size="24px" />
            <AddEmailText>Add another email</AddEmailText>
          </AddEmailButton>
        )}
      </LinkShareSection>

      {/* Back and Send Invitation Buttons */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "24px",
          marginTop: "32px",
          width: "100%",
          maxWidth: "400px",
        }}
      >
        <ButtonGroup style={{ maxWidth: "400px" }}>
          <BackButton
            type="button"
            onClick={onBack}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            height="40px"
            style={{
              background: "#e9f0fe",
              borderRadius: "32px",
              padding: "8px 16px",
              width: "100px",
              fontSize: "14px",
              fontWeight: 700,
              color: "#000e2a",
              border: "none",
            }}
          >
            <Icon component={icons.back} />
            Back
          </BackButton>
          <PrimaryButton
            type="button"
            onClick={handleSubmit}
            disabled={!hasValidEmails}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            height="40px"
            fontSize="16px"
            style={{
              flex: 1,
              borderRadius: "32px",
              padding: "8px 16px",
              background: "linear-gradient(to right, #2266ef 0%, #6c2ef2 100%)",
              color: "white",
              fontWeight: 700,
            }}
          >
            Send Invitation
          </PrimaryButton>
        </ButtonGroup>

        {/* Skip Text */}
        <SkipText onClick={handleSkip}>Skip</SkipText>
      </div>
    </motion.div>
  );
};

export default Step3InviteTeam;
